import BaseService from './BaseService'
import { getAxiosInterceptor, getHeader } from '../util/axios_helper'
import servicesConfig, { CustomAxiosHeaders } from '../config/services'

import { AxiosResponse } from 'axios'
import axios from 'axios'
import { getV1JWTWithoutRefresh } from '../util/jwt_helper'
import { requests } from '.'
import store from '@/store'

// Configure request setup
const serviceName = 'OauthService'
const host = servicesConfig.Oauth
const opts: CustomAxiosHeaders = { host, serviceName, useNewToken: true }
const headers = getHeader()

// Create two axios instances to match the class behavior
const createDefaultAxiosInstance = () => {
  const instance = axios.create({
    baseURL: host,
  })

  instance.interceptors.request.use(getAxiosInterceptor(headers, true, false))

  return instance
}

const createSecondaryAxiosInstance = () => {
  return axios.create({
    baseURL: host,
  })
}

const createNoJWTAxiosInstance = () => {
  return axios.create({
    baseURL: host,
  })
}

const defaultAxiosInstance = createDefaultAxiosInstance()
const secondaryAxiosInstance = createSecondaryAxiosInstance()
const noJWTInstance = createNoJWTAxiosInstance()

// Define request functions for both instances
const defaultRequest = (addlOpts?: CustomAxiosHeaders) => {
  return requests(Object.assign({}, { ...opts, _axiosInstance: defaultAxiosInstance }, addlOpts))
}

const secondaryRequest = (addlOpts?: CustomAxiosHeaders) => {
  return requests(Object.assign({}, { ...opts, _axiosInstance: secondaryAxiosInstance }, addlOpts))
}

const noJWTRequest = (addlOpts?: CustomAxiosHeaders) => {
  return requests(Object.assign({}, { ...opts, _axiosInstance: noJWTInstance }, addlOpts))
}

// Helper function to handle response
const responseBody = (res: AxiosResponse) => res.data

// Request options
const options = {
  headers,
}

// Define service object with methods
const OauthService = {
  loginWithEmail: (body: { [key: string]: any }, url = 'email') =>
    secondaryRequest().post(`/2/login/${url}`, body, options).then(responseBody),

  forgotPassword: (body: { email: string; companyId?: string }) =>
    secondaryRequest().post(`/users/forgotPassword`, body).then(responseBody),

  changePassword: (body: {
    userId?: string
    newPassword: string
    token: string
    companyId?: string
    setToAll?: boolean
  }) => {
    if (!body.userId) {
      throw 'UserId needed'
    }
    return secondaryRequest().post(`/users/changePassword`, body, options).then(responseBody)
  },

  validateForgotPasswordLink: (body: { userId: string; token: string; companyId: string }) => {
    if (!body.userId) {
      throw 'UserId needed'
    }
    return secondaryRequest()
      .get(`/users/forgotPassword/validateLink?token=${body.token}&companyId=${body.companyId}&userId=${body.userId}`)
      .then(responseBody)
  },

  refreshToken: async (refresh_token: string, newRefreshToken?: string) => {
    return noJWTInstance({
      url: `${host}/2/login/token`,
      method: 'POST',
      headers: { ...headers, 'refresh-token': refresh_token },
      data: { refreshTokenV2: newRefreshToken },
    }).then(responseBody)
  },

  refreshJwt: async (refresh_token: string) => {
    return secondaryAxiosInstance({
      url: `${host}/refresh`,
      method: 'POST',
      headers: { ...headers, 'refresh-token': refresh_token },
    }).then(responseBody)
  },

  loginAsProxy: (userId: string) => {
    return defaultRequest().post(`/2/login/proxy`, { userId }, options).then(responseBody)
  },

  getCurrentUser: async () => {
    const deviceId = await store.dispatch('getDeviceId')
    return defaultRequest()
      .get(`/2/login/current`, {
        headers: {
          'device-id': deviceId,
          ...headers,
        },
      })
      .then(responseBody)
  },

  getSettings: async (companyId: string) => {
    return defaultRequest().get(`/whitelabel/${companyId}`).then(responseBody)
  },

  getCustomPages: async (companyId: string, locationId?: string) => {
    return defaultRequest()
      .get(`/sidenav-custom-pages?companyId=${companyId}${locationId ? `&locationId=${locationId}` : ''}`)
      .then(responseBody)
  },

  getUserSessionJWT: async (appId: string, locationId?: string) => {
    return defaultRequest()
      .get(`/sso/${appId}/session-details`, {
        headers: {
          ...(locationId && { location: locationId }),
        },
      })
      .then(responseBody)
  },

  signout: async (sessionId: string) => {
    try {
      return noJWTRequest()
        .post(
          `/signout`,
          {},
          {
            headers: {
              ...headers,
              'session-id': sessionId,
              authorization: `Bearer ${getV1JWTWithoutRefresh()}`,
            },
          }
        )
        .then(responseBody)
    } catch (error) {
      console.error('Error signing out', error)
    }
  },

  signinRefresh: async (activeLocations: string) => {
    return defaultRequest()
      .post(`/2/login/signin/refresh?version=2&location_id=${activeLocations}`, {})
      .then(responseBody)
  },

  getSSOConfigByEmail: async (email: string) => {
    return defaultRequest().get(`/sso/agency/config?email=${email}`).then(responseBody)
  },

  getSSOConfigByCompanyId: async (companyId: string) => {
    return defaultRequest().get(`/sso/agency/config?companyId=${companyId}`).then(responseBody)
  },

  // Get access token from code & state (companyId)
  getAccessToken: async (payload: {
    code: string
    companyId: string
    deviceId: string
    deviceType: string
    deviceName: string
    testSessionId: string
  }) => {
    return defaultRequest().post(`/sso/agency/login`, payload).then(responseBody)
  },

  getKey: async (locationId: string) => {
    return defaultRequest().get(`/keys/location/${locationId}`).then(responseBody)
  },

  getCannySSOLoginToken: async (userId: string) => {
    return defaultRequest()
      .get(`/2/login/canny?userId=${userId}`, {
        headers: {
          ...headers,
        },
      })
      .then(responseBody)
  },

  getListOfIntegrations: async (requestParams: Record<string, any>) => {
    return defaultRequest()
      .get('/integration', {
        params: requestParams,
      })
      .then(responseBody)
  },

  loginForSupportAgent: async (payload: {
    userId: string
    secret: string
    supportAgentId: string
    deviceId?: string
    deviceName?: string
    deviceType?: string
  }) => {
    return noJWTInstance({
      url: `${host}/2/login/supportAgent`,
      method: 'POST',
      data: payload,
    }).then(responseBody)
  },

  generateMagicTokenForDesktop: async (params: { deviceId: string }) => {
    return defaultRequest().post('/auth/desktop/generate-token', params).then(responseBody)
  },
  
  updateSessionToken: async (apiKey: string, userId: string, token: string) => {
    return defaultRequest()
      .post(
        `/users/${userId}/sessions/token`,
        { token: token },
        {
          headers: {
            ...headers,
            'session-id': apiKey,
          },
        }
      )
      .then(responseBody)
  },
}

export default OauthService
